import React, { FunctionComponent, useState, useContext, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { makeStyles } from '@mui/styles';
import { Button, Grid, Link, Box, Checkbox, FormControlLabel } from '@mui/material';
import { GOOGLE_API_KEY } from '../../utils/env';
import { SystemAccessoriesContext } from '../../adminSettings/contexts/SystemAccessoriesContext';
import TextInput from '../../form/components/TextInput';
import SelectInput from '../../form/components/SelectInput';
import { EstimateDetailsContext } from '../../estimateDetails/contexts/EstimateDetailsContext';
import { EstimateCostsContext } from '../contexts/EstimateCostsContext';

const containerStyle = {
  width: '400px',
  height: '400px',
  marginBottom: '20px',
};

const useStyles = makeStyles((theme: any) => ({
  mapContainer: {
    '@media (max-width: 1200px)': {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 20,
    },
  },
}));

const NewCustomerForm = (props) => {
  const history = useHistory();
  const { salesEstimateId } = props;

  const classes = useStyles();

  const [curbFormValues, setCurbFormValues] = React.useState<Record<string, string>[]>([]);
  const [craneFormValues, setCraneFormValues] = React.useState<Record<string, string>>({});

  const { salesEstimate } = React.useContext(EstimateDetailsContext);
  const { updateCraneDetails, craneDetails, updateCurbAdapter } = React.useContext(EstimateCostsContext);

  React.useEffect(() => {
    setCraneFormValues({
      unit_to_building_edge: craneDetails?.unit_to_building_edge,
      unit_to_center_pin: craneDetails?.unit_to_center_pin,
      building_height: craneDetails?.building_height,
      max_unit_weight: craneDetails?.max_unit_weight,
      estimated_cost: craneDetails?.estimated_cost,
    });
  }, [craneDetails]);

  useEffect(() => {
    if (salesEstimate?.sales_estimate_assets) {
      setCurbFormValues(
        salesEstimate.sales_estimate_assets.map((asset) => ({
          previous_asset_lookup_name: asset.curb_adapter?.previous_asset_lookup_name,
          replacement_asset_lookup_name: asset.curb_adapter?.replacement_asset_lookup_name,
          cost: asset.curb_adapter?.cost,
          submittal_url: asset.curb_adapter?.submittal_url,
          fits_existing: asset.curb_adapter?.fits_existing,
        })),
      );
    }
  }, [salesEstimate]);

  const handleCurbValueChange = (index, field, value) => {
    const updatedCurbFormValues = [...curbFormValues];
    updatedCurbFormValues[index][field] = value;
    setCurbFormValues(updatedCurbFormValues);
  };

  const handleCraneSubmit = () => {
    updateCraneDetails(salesEstimateId, craneFormValues);
  };

  const handleCurbSubmit = (salesEstimateAssetId, index) => {
    updateCurbAdapter(salesEstimateAssetId, curbFormValues[index]);
  };

  return (
    <>
      <Grid container justifyContent="space-between">
        <Grid item xs={12}>
          <h2 style={{ fontSize: '18px', marginBottom: 10 }}>Crane Costs</h2>
        </Grid>
        <Grid item xs={12} lg={6} className={classes.mapContainer}>
          <GoogleMap mapContainerStyle={containerStyle} center={salesEstimate?.address_coordinates} zoom={20}>
            <Marker position={salesEstimate?.address_coordinates} />
          </GoogleMap>
        </Grid>
        <br />
        <Grid item xs={12} lg={6}>
          <Grid container justifyContent="space-between">
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <TextInput label="Site Address" value={salesEstimate?.address} shrinkLabel />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <TextInput
                label="Unit to Building Edge (Tooltip)"
                value={craneFormValues.unit_to_building_edge}
                valueChanged={(value) => setCraneFormValues({ ...craneFormValues, unit_to_building_edge: value })}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <TextInput
                label="Unit to Center Pin (Tooltip)"
                value={craneFormValues.unit_to_center_pin}
                valueChanged={(value) => setCraneFormValues({ ...craneFormValues, unit_to_center_pin: value })}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <TextInput
                label="Building Height (Tooltip)"
                value={craneFormValues.building_height}
                valueChanged={(value) => setCraneFormValues({ ...craneFormValues, building_height: value })}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <TextInput
                label="Maximum Unit Weight"
                value={craneFormValues.max_unit_weight}
                valueChanged={(value) => setCraneFormValues({ ...craneFormValues, max_unit_weight: value })}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: 20 }}>
              <TextInput
                label="Estimated Cost"
                value={craneFormValues.estimated_cost}
                valueChanged={(value) => setCraneFormValues({ ...craneFormValues, estimated_cost: value })}
                shrinkLabel
                currency
              />
            </Grid>
            <Grid item xs={12}>
              <br />
              <Button variant="contained" onClick={handleCraneSubmit} type="button">
                Save Crane Details
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <br />
      <hr />
      <br />
      <Grid container justifyContent="space-between" spacing={3}>
        <Grid item xs={12}>
          <h2 style={{ fontSize: '18px', marginBottom: 10 }}>Curb Adapters</h2>
        </Grid>
        {salesEstimate?.sales_estimate_assets?.map((asset, index) => (
          <Grid item key={index} xs={12} md={6}>
            <Grid container justifyContent="space-between">
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                <TextInput label="Asset" value={asset.asset_number} shrinkLabel />
              </Grid>
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                <TextInput
                  label="Previous Model Name"
                  value={curbFormValues[index]?.previous_asset_lookup_name}
                  disabled={!!curbFormValues[index]?.fits_existing}
                  valueChanged={(value) => handleCurbValueChange(index, 'previous_asset_lookup_name', value)}
                  shrinkLabel
                />
              </Grid>
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                <TextInput
                  label="Replacement Model Name"
                  value={curbFormValues[index]?.replacement_asset_lookup_name}
                  disabled={!!curbFormValues[index]?.fits_existing}
                  valueChanged={(value) => handleCurbValueChange(index, 'replacement_asset_lookup_name', value)}
                  shrinkLabel
                />
              </Grid>
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                <TextInput
                  label="Curb Adapter Cost"
                  value={curbFormValues[index]?.cost}
                  disabled={!!curbFormValues[index]?.fits_existing}
                  valueChanged={(value) => handleCurbValueChange(index, 'cost', value)}
                  shrinkLabel
                  currency
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  label="Submittal URL"
                  value={curbFormValues[index]?.submittal_url}
                  disabled={!!curbFormValues[index]?.fits_existing}
                  valueChanged={(value) => handleCurbValueChange(index, 'submittal_url', value)}
                  shrinkLabel
                />
              </Grid>
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!curbFormValues[index]?.fits_existing}
                      onChange={(event) => handleCurbValueChange(index, 'fits_existing', event.target.checked)}
                      name="fits_existing"
                      color="primary"
                    />
                  }
                  label="New Unit Fits Existing Curb"
                />
              </Grid>
              <Grid item xs={12} style={{ marginBottom: 20 }}>
                <Button variant="contained" onClick={() => handleCurbSubmit(asset.id, index)} type="button">
                  Save Curb Details
                </Button>
                <Button
                  variant="contained"
                  style={{ marginLeft: 10 }}
                  onClick={() =>
                    window.open(
                      'https://micrometlwebsite.blob.core.windows.net/micrometlwebsiteimages/CA-TRN-593-TRN-592-11.pdf',
                      '_blank',
                    )
                  }
                  type="button"
                >
                  Preview Document
                </Button>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <br />
      <hr />
      <br />
      <Button
        variant="contained"
        color="primary"
        onClick={() => history.push(`/rooftop_automation/${salesEstimateId}/installation_costs`)}
      >
        Submit Crane and Curb Adapters
      </Button>
    </>
  );
};

export default NewCustomerForm;
